// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {useInfiniteQuery, useQuery} from 'react-query';
import pLimit from 'p-limit';
import useQueryErrorHandler from './useQueryErrorHandler';
import {
    handleResponse,
    searchResources,
} from '../../API/Handlers/ResourceGraphQLHandler';
import {wrapRequest} from '../../Utils/API/HandlerUtils';
import {getStatus} from '../../Utils/StatusUtils';
import {processResourcesError} from '../../Utils/ErrorHandlingUtils';
import {FUNCTION_RESPONSE_SIZE_TOO_LARGE} from '../../config/constants';
import * as R from 'ramda';

export const searchQueryKey = 'resourcesSearch';
export const useResourcesSearch = (
    text = '',
    pageSize = 25,
    accounts = [],
    resourceTypes = [],
    config = {}
) => {
    const {handleError} = useQueryErrorHandler();

    const userFilters = {
        text,
    };

    if (accounts?.length > 0) userFilters.accounts = accounts;
    if (resourceTypes?.length > 0) userFilters.resourceTypes = resourceTypes;

    const fetchResults = ({pageParam = 0}) =>
        wrapRequest(processResourcesError, searchResources, {
            ...userFilters,
            pagination: {
                start: pageSize * pageParam,
                end: pageSize * pageParam + pageSize,
            },
        })
            .then(handleResponse)
            .then(
                R.pathOr([], ['body', 'data', 'searchResources', 'resources'])
            );

    const {
        isLoading,
        isError,
        data,
        refetch,
        isFetched,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery(
        [searchQueryKey, text, accounts, resourceTypes, pageSize],
        fetchResults,
        {
            onError: handleError,
            keepPreviousData: true,
            refetchInterval: false,
            getNextPageParam: (lastPage, allPages) =>
                lastPage.length > 0 ? allPages.length : undefined,
            ...config,
        }
    );

    const flattened = R.flatten(data?.pages ?? []);

    return {
        data: flattened,
        isLoading,
        isFetching: isFetching || isFetchingNextPage,
        isFetched,
        isError,
        refetch,
        hasNextPage,
        fetchNextPage,
        status: getStatus(isFetching, isError),
    };
};
export const searchPaginatedQueryKey = 'searchPaginatedQueryKey';
export const useResourcesSearchPaginated = (
    text = '',
    pagination = {start: 0, end: 10},
    accounts = [],
    resourceTypes = [],
    config = {}
) => {
    const {handleError} = useQueryErrorHandler();

    const userFilters = {
        text,
        pagination,
    };

    if (accounts?.length > 0) userFilters.accounts = accounts;
    if (resourceTypes?.length > 0) userFilters.resourceTypes = resourceTypes;

    const {isLoading, isError, data, refetch, isFetching} = useQuery(
        [searchPaginatedQueryKey, text, accounts, resourceTypes, pagination],
        () =>
            wrapRequest(processResourcesError, searchResources, userFilters)
                .then(handleResponse)
                .then(R.pathOr([], ['body', 'data', 'searchResources'])),
        {
            onError: handleError,
            keepPreviousData: true,
            refetchInterval: false,
            ...config,
        }
    );

    return {
        data: data?.resources,
        count: data?.count,
        isLoading,
        isFetching,
        isError,
        refetch,
        status: getStatus(isFetching, isError),
    };
};

export const useResourcesProgressiveLoad = (
    text = '',
    accounts = [],
    resourceTypes = [],
    config = {}
) => {
    const {handleError} = useQueryErrorHandler();
    const maxResources = 10000;
    const initialBatchSize = 1000;
    const parallelBatchSize = 5;
    const minRangeSize = 25;
    const maxSplitDepth = 12;
    const requestTimeOut = 20000;

    const userFilters = {
        text,
    };

    if (accounts?.length > 0) userFilters.accounts = accounts;
    if (resourceTypes?.length > 0) userFilters.resourceTypes = resourceTypes;

    const withTimeout = (promise, ms, label, signal) => {
        let timeoutId;
        const timeOutPromise = new Promise((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
            if (signal) {
                if (signal.aborted) {
                    clearTimeout(timeoutId);
                    reject(new Error('Query aborted'));
                    return;
                }
                signal.addEventListener(
                    'abort',
                    () => {
                        clearTimeout(timeoutId);
                        reject(new Error('Query aborted'));
                    },
                    {once: true}
                );
            }
        });
        return Promise.race([promise, timeOutPromise]).finally(() => clearTimeout(timeoutId));
    };

    const limiter = pLimit(parallelBatchSize);

    const fetchRawRange = async (start, end, signal) => {
        const req = wrapRequest(processResourcesError, searchResources, {
            ...userFilters,
            pagination: {start, end},
        })
            .then(handleResponse)
            .then(R.pathOr([], ['body', 'data', 'searchResources', 'resources']));

        return withTimeout(req, requestTimeOut, `searchResources(${start},${end})`, signal);
    };

    const fetchRangeSafe = async (start, end, signal, depth = 0) => {
        const size = end - start;
        try {
            return await limiter(() => fetchRawRange(start, end, signal));
        } catch (err) {
            const errorMessage = err?.message || err?.toString() || '';
            const isPayloadError = errorMessage.includes(FUNCTION_RESPONSE_SIZE_TOO_LARGE);
            const canSplit = isPayloadError && size > minRangeSize && depth < maxSplitDepth;

            if (!canSplit) throw err;

            const mid = start + Math.floor(size / 2);
            const [left, right] = await Promise.all([
                fetchRangeSafe(start, mid, signal, depth + 1),
                fetchRangeSafe(mid, end, signal, depth + 1),
            ]);
            return [...left, ...right];
        }
    };

    const {
        isLoading,
        isError,
        data,
        refetch,
        isFetched,
        isFetching,
    } = useQuery(
        ['resourcesProgressiveLoad', text, accounts, resourceTypes],
        async (ctx = {}) => {
            const signal = ctx.signal;
            const maxBatches = Math.ceil(maxResources / initialBatchSize);
            const allResources = [];

            for (let baseBatchIndex = 0; baseBatchIndex < maxBatches; baseBatchIndex += parallelBatchSize) {
                const ranges = Array.from({length: Math.min(parallelBatchSize, maxBatches - baseBatchIndex)}, (_, i) => {
                    const batchIdx = baseBatchIndex + i;
                    const start = batchIdx * initialBatchSize;
                    const end = Math.min(start + initialBatchSize, maxResources);
                    return {start, end};
                });

                const waveResults = await Promise.all(
                    ranges.map((r) => fetchRangeSafe(r.start, r.end, signal))
                );

                for (const resources of waveResults) {
                    if (!Array.isArray(resources) || resources.length === 0) {
                        return {
                            resources: allResources,
                            totalLoaded: allResources.length,
                            reachedMaxLimit: false,
                        };
                    }

                    const remaining = maxResources - allResources.length;
                    allResources.push(...resources.slice(0, remaining));

                    if (allResources.length >= maxResources) {
                        return {
                            resources: allResources,
                            totalLoaded: allResources.length,
                            reachedMaxLimit: true,
                        };
                    }

                    if (resources.length < (ranges[0].end - ranges[0].start)) {
                        return {
                            resources: allResources,
                            totalLoaded: allResources.length,
                            reachedMaxLimit: false,
                        };
                    }
                }
            }

            return {
                resources: allResources,
                totalLoaded: allResources.length,
                reachedMaxLimit: allResources.length >= maxResources,
            };
        },
        {
            onError: handleError,
            keepPreviousData: true,
            refetchInterval: false,
            cacheTime: 5 * 60 * 1000,
            staleTime: 2 * 60 * 1000,
            ...config,
        }
    );

    return {
        resources: data?.resources ?? [],
        isLoading,
        isFetching,
        isFetched,
        isError,
        isComplete: isFetched && !isLoading,
        reachedMaxLimit: data?.reachedMaxLimit ?? false,
        maxResources,
        totalLoaded: data?.totalLoaded ?? 0,
        refetch,
        hasNextPage: false,
        fetchNextPage: () => {},
        status: getStatus(isFetching, isError),
    };
};
